import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useHttpClient } from '@bonusly/http-client';

import { Initiative, InitiativeFormData } from '../types';

export const useInitiatives = (enabled: boolean = true) => {
	const http = useHttpClient();

	return useQuery({
		queryKey: ['achieve/initiatives'],
		queryFn: async () => {
			const response = await http.get<Initiative[]>('/achieve/initiatives', {
				headers: {
					'Key-Inflection': 'camel',
				},
			});
			return response.data.filter((initiative) => !initiative.deleted_at);
		},
		enabled,
	});
};

export const useInitiative = (id: string) => {
	const http = useHttpClient();

	return useQuery({
		queryKey: ['achieve/initiatives', id],
		queryFn: async () => {
			const response = await http.get<Initiative>(`/achieve/initiatives/${id}`, {
				headers: {
					'Key-Inflection': 'camel',
				},
			});
			return response.data;
		},
		enabled: !!id,
	});
};

export const useCreateInitiative = () => {
	const queryClient = useQueryClient();
	const http = useHttpClient();

	return useMutation({
		mutationFn: async (data: InitiativeFormData) => {
			const response = await http.post<Initiative>('/achieve/initiatives', data);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['achieve/initiatives'] });
		},
	});
};

export const useUpdateInitiative = () => {
	const queryClient = useQueryClient();
	const http = useHttpClient();

	return useMutation({
		mutationFn: async ({ id, data }: { id: string; data: Partial<InitiativeFormData> }) => {
			const response = await http.patch<Initiative>(`/achieve/initiatives/${id}`, data);
			return response.data;
		},
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: ['achieve/initiatives'] });
			queryClient.invalidateQueries({ queryKey: ['achieve/initiatives', variables.id] });
		},
	});
};

export const useDeleteInitiative = () => {
	const queryClient = useQueryClient();
	const http = useHttpClient();

	return useMutation({
		mutationFn: async (id: string) => {
			await http.delete(`/achieve/initiatives/${id}`);
			return id;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['achieve/initiatives'] });
		},
	});
};
